import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { Label } from '@avanta/shared/components/ui/Label';
import { Textarea } from '@avanta/shared/components/ui/Textarea';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { type Appointment } from 'types/appointments';
import { RatingStars } from './RatingStars';
import { useRateAppointment } from './useRateAppointment';

type RatingFormData = {
  rating: number;
  comment: string;
};

export const RateAppointment = ({
  appointment,
  canClose = true,
}: {
  appointment: Appointment;
  canClose?: boolean;
}): React.ReactElement => {
  const { mutate: rateAppointment } = useRateAppointment();
  const { trackTherapySessionRated } = useTrackingEvents();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<RatingFormData>({
    defaultValues: {
      rating: 0,
      comment: '',
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: RatingFormData): Promise<void> => {
    if (appointment?.id && appointment.sessionDetails?.id) {
      const body = {
        psychologist_id: appointment.psychologistId,
        patient_id: appointment.patientId,
        session: {
          ...appointment.sessionDetails,
          rate: {
            rate: data.rating,
            description: data.comment,
            userId: appointment.patientId,
          },
        },
      };
      trackTherapySessionRated({
        psychologistId: appointment.psychologistId,
        rate: data.rating,
        comment: data.comment,
      });
      rateAppointment(body);
    }
  };

  const centerName = `${appointment.center?.name} ${appointment.center?.city ? `(${appointment.center?.city})` : ''}`;
  const psychologistName = `${appointment.psychologist.psychologist.name} ${appointment.psychologist.psychologist.surname}`;

  return (
    <Dialog
      title={t('RateAppointment.heading')}
      description={t('RateAppointment.body', {
        center: centerName,
        psychologist: psychologistName,
      })}
      {...(canClose && {
        secondaryAction: {
          label: t('RateAppointment.cancel'),
          variant: 'terciary',
        },
      })}
      primaryAction={{
        label: t('RateAppointment.cta'),
        action: handleSubmit(onSubmit),
        variant: 'secondary',
        disabled: !isValid,
      }}
      open
      hideClose
      enableOutsideClick={canClose}
    >
      <form className="space-y-6">
        <div className="py-5">
          <Controller
            name="rating"
            control={control}
            rules={{ required: true, min: 1 }}
            render={({ field }) => <RatingStars value={field.value} onChange={field.onChange} />}
          />
        </div>
        <div>
          <Label htmlFor="appointment-rating-textarea" className="mb-2 block">
            {t('RateAppointment.Form.comment.label')}
          </Label>
          <Textarea
            id="appointment-rating-textarea"
            placeholder={t('RateAppointment.Form.comment.placeholder')}
            rows={4}
            {...register('comment')}
          />
        </div>
      </form>
    </Dialog>
  );
};
